/**
 * One student's attendance history, day by day, with every field that was recorded.
 *
 * The pickup roster shows five squares per student because five is what fits next to a
 * name. This is the way back to everything the squares compress away — who checked the
 * child in, who collected them, at what time, and what happened on the days before the
 * ones on screen — so the roster never has to grow a sixth square to answer a question.
 *
 * Scoped exactly as the roster that opens it: **active enrollments in after-school
 * programs**. The roster reaches this population through `programs.type = 'after-school'`
 * + `enrollments.is_active`, so a timeline that read every row for the student would
 * answer a different question than the square that was clicked — most of the history in
 * the table is summer camp, which is not what an admin working dismissal is asking about.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import {
  bestOf,
  deriveSlotStatus,
  monthStart,
  type AttendanceSlotStatus,
} from '../utils/attendanceSlot';
import {
  REAL_SESSION_KEY,
  toAttendanceRecord,
  type AttendanceRecord,
  type AttendanceRecordRow,
} from '../types/attendance';

/** Sessions added per "show earlier" step. */
export const TIMELINE_PAGE_SIZE = 10;

/**
 * Rows fetched per session asked for. A student holds one attendance row per concurrent
 * enrollment; four is generous headroom over the two a dual-enrolled student produces,
 * and lets the whole page come back in one query rather than one per day.
 */
const ROWS_PER_SESSION = 4;

/** One attendance row, plus the program it belongs to. */
export interface TimelineRecord extends AttendanceRecord {
  programName: string | null;
}

/** One session day for this student. */
export interface TimelineDay {
  /** YYYY-MM-DD, business TZ. */
  date: string;
  /** The same summary the roster square shows for this day. */
  status: AttendanceSlotStatus;
  /** Every program's record for the day — usually one, two when dual-enrolled. */
  records: TimelineRecord[];
}

export interface StudentAttendanceTimeline {
  days: TimelineDay[];
  /** True when older sessions exist beyond the ones returned. */
  hasMore: boolean;
}

interface RecordRowWithProgram extends AttendanceRecordRow {
  // Non-null in practice: both embeds are inner joins, so a row without them is filtered
  // out rather than returned with a null.
  program: { name: string; type: string } | null;
  enrollment: { is_active: boolean } | null;
}

interface FlexEnrollmentRow {
  id: string;
  programs: { billing_type: string; type: string } | null;
}

export const studentTimelineKeys = {
  all: (studentId: string, sessions: number) =>
    ['student-attendance-timeline', studentId, sessions] as const,
};

/**
 * `sessions` is how many session days to return, newest first. Paging is deliberately
 * "fetch a bigger page" rather than a cursor: the pages are tiny, and re-reading them
 * keeps a re-opened modal consistent with a roster that may have moved on.
 */
export function useStudentAttendanceTimeline(studentId: string, sessions: number) {
  return useQuery<StudentAttendanceTimeline>({
    queryKey: studentTimelineKeys.all(studentId, sessions),
    queryFn: async () => {
      const rowLimit = sessions * ROWS_PER_SESSION;
      // `!inner` makes both embeds filterable joins rather than optional lookups, so the
      // scoping happens in the query instead of being thrown away after paging — filtering
      // client-side would let camp rows consume the row budget and starve the page.
      const { data, error } = await supabase
        .from('attendance_records')
        .select('*, program:programs!inner(name, type), enrollment:enrollments!inner(is_active)')
        .eq('student_id', studentId)
        .eq('program.type', 'after-school')
        .eq('enrollment.is_active', true)
        // A virtual record's program embed reads 'after-school' — it carries the child's
        // after-school program_id — so the type filter above does not exclude it. Without
        // this the timeline would show three rows a day for one child and spend its row
        // budget on Chinese and Math instead of the history being asked for.
        .eq('virtual_class_key', REAL_SESSION_KEY)
        .order('session_date', { ascending: false })
        .limit(rowLimit);
      if (error) throw error;

      const rows = (data ?? []) as unknown as RecordRowWithProgram[];

      // Group by day, newest first. The query's ordering carries through the Map.
      const byDate = new Map<string, TimelineRecord[]>();
      for (const row of rows) {
        const list = byDate.get(row.session_date) ?? [];
        list.push({ ...toAttendanceRecord(row), programName: row.program?.name ?? null });
        byDate.set(row.session_date, list);
      }

      const allDates = Array.from(byDate.keys());
      // Two independent reasons more history may exist: we saw more days than were asked
      // for, or we hit the row cap and the next row could be a new day.
      const hasMore = allDates.length > sessions || rows.length === rowLimit;
      const dates = allDates.slice(0, sessions);

      if (dates.length === 0) return { days: [], hasMore: false };

      // Flex plans, so an unselected day reads as "not scheduled" here exactly as it does
      // on the roster square that was clicked to get here.
      // Same scoping as the records above: a Flex enrollment the student has since left
      // must not make their current days read as "not scheduled".
      const { data: enrollmentData, error: enrollmentError } = await supabase
        .from('enrollments')
        .select('id, programs!inner(billing_type, type)')
        .eq('student_id', studentId)
        .eq('is_active', true)
        .eq('programs.type', 'after-school');
      if (enrollmentError) throw enrollmentError;

      const flexEnrollmentIds = ((enrollmentData ?? []) as unknown as FlexEnrollmentRow[])
        .filter(e => e.programs?.billing_type === 'flex_monthly')
        .map(e => e.id);

      const flexDays = new Set<string>();
      const flexMonths = new Set<string>();
      if (flexEnrollmentIds.length > 0) {
        const planMonths = Array.from(new Set(dates.map(monthStart)));
        const { data: planData, error: planError } = await supabase
          .from('flex_month_plans')
          .select('plan_month, dates')
          .in('enrollment_id', flexEnrollmentIds)
          .in('plan_month', planMonths);
        if (planError) throw planError;

        for (const plan of (planData ?? []) as { plan_month: string; dates: string[] | null }[]) {
          flexMonths.add(plan.plan_month.slice(0, 10));
          for (const date of plan.dates ?? []) flexDays.add(date);
        }
      }

      const days: TimelineDay[] = dates.map(date => {
        const records = byDate.get(date) ?? [];
        const recordStatus = records
          .map(r => r.status)
          .reduce<AttendanceRecord['status'] | undefined>(
            (best, status) => (best === undefined ? status : bestOf(best, status)),
            undefined,
          );
        return {
          date,
          status: deriveSlotStatus({
            date,
            isFlex: flexEnrollmentIds.length > 0,
            flexMonths,
            flexDays,
            recordStatus,
          }),
          records,
        };
      });

      return { days, hasMore };
    },
    enabled: Boolean(studentId),
    staleTime: 30_000,
  });
}
